import { useEffect } from "react";

interface SeoHeadProps {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalUrl?: string;
  keywords?: string[];
  schemaMarkup?: Record<string, any>;
  noIndex?: boolean;
}

/**
 * Dynamically sets SEO meta tags in document head.
 * Cleans up on unmount to prevent leaking to other pages.
 */
const SeoHead = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  canonicalUrl,
  keywords,
  schemaMarkup,
  noIndex = false,
}: SeoHeadProps) => {
  useEffect(() => {
    const cleanups: (() => void)[] = [];

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      const prev = el?.getAttribute("content") || null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        cleanups.push(() => el && document.head.removeChild(el));
      } else {
        cleanups.push(() => {
          if (prev !== null) el!.setAttribute("content", prev);
          else el!.remove();
        });
      }
      el.setAttribute("content", content);
    };

    // Title
    const prevTitle = document.title;
    if (title) document.title = title;
    cleanups.push(() => { document.title = prevTitle; });

    // Description
    if (description) setMeta("description", description);

    // Keywords
    if (keywords?.length) setMeta("keywords", keywords.join(", "));

    // Open Graph
    if (ogTitle || title) setMeta("og:title", ogTitle || title || "", true);
    if (ogDescription || description) setMeta("og:description", ogDescription || description || "", true);
    if (ogImage) setMeta("og:image", ogImage, true);
    if (canonicalUrl) setMeta("og:url", canonicalUrl, true);

    // Twitter
    if (ogTitle || title) setMeta("twitter:title", ogTitle || title || "");
    if (ogDescription || description) setMeta("twitter:description", ogDescription || description || "");
    if (ogImage) setMeta("twitter:image", ogImage);

    // Canonical
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      const prevHref = link?.getAttribute("href") || null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        cleanups.push(() => link && document.head.removeChild(link));
      } else {
        cleanups.push(() => {
          if (prevHref) link!.setAttribute("href", prevHref);
        });
      }
      link.setAttribute("href", canonicalUrl);
    }

    // NoIndex
    if (noIndex) {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex, nofollow";
      document.head.appendChild(meta);
      cleanups.push(() => document.head.removeChild(meta));
    }

    // JSON-LD Schema
    if (schemaMarkup) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schemaMarkup);
      document.head.appendChild(script);
      cleanups.push(() => document.head.removeChild(script));
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, ogTitle, ogDescription, ogImage, canonicalUrl, keywords, schemaMarkup, noIndex]);

  return null;
};

export default SeoHead;
